import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Palette, Plus, Trash2, Pencil, Package, Image as ImageIcon,
  ChevronRight, Check, X, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CTA_OPTIONS = [
  "Jetzt bewerben",
  "Mehr erfahren",
  "Angebot einholen",
  "Unverbindlich Termin vereinbaren",
  "Kein CTA"
];

const LOGO_POSITIONS = [
  { value: "top-left", label: "Oben links" },
  { value: "top-right", label: "Oben rechts" },
  { value: "bottom-left", label: "Unten links" },
  { value: "bottom-right", label: "Unten rechts" },
  { value: "ai_decides", label: "KI entscheidet" }
];

const EMPTY_KIT = {
  name: "",
  colors: [],
  logo_ids: [],
  logo_position: "top-right",
  image_preferences: "",
  default_cta: "Mehr erfahren",
  tone: "",
  preferred_font: null,
  notes: ""
};

export default function BrandKitsPage() {
  const navigate = useNavigate();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logos, setLogos] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_KIT });
  const [saving, setSaving] = useState(false);

  const fetchKits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brand-kits`);
      setKits(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Fehler beim Laden der Brand Kits");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logos`);
      setLogos(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchKits();
    fetchLogos();
  }, [fetchKits, fetchLogos]);

  const openEditor = (kit = null) => {
    if (kit) {
      setEditingKit(kit);
      setFormData({
        name: kit.name || "",
        colors: kit.colors || [],
        logo_ids: kit.logo_ids || [],
        logo_position: kit.logo_position || "top-right",
        image_preferences: kit.image_preferences || "",
        default_cta: kit.default_cta || "Mehr erfahren",
        tone: kit.tone || "",
        preferred_font: kit.preferred_font || null,
        notes: kit.notes || ""
      });
    } else {
      setEditingKit(null);
      setFormData({ ...EMPTY_KIT });
    }
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Bitte einen Namen eingeben");
      return;
    }
    setSaving(true);
    try {
      if (editingKit) {
        await axios.put(`${API}/brand-kits/${editingKit.id}`, formData);
        toast.success("Brand Kit aktualisiert!");
      } else {
        await axios.post(`${API}/brand-kits`, formData);
        toast.success("Brand Kit erstellt!");
      }
      setShowEditor(false);
      fetchKits();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (kitId) => {
    if (!window.confirm("Brand Kit wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/brand-kits/${kitId}`);
      toast.success("Brand Kit gelöscht");
      setKits(prev => prev.filter(k => k.id !== kitId));
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const addColor = () => {
    setFormData(prev => ({
      ...prev,
      colors: [...prev.colors, { hex: "#4CAF50", name: "Neue Farbe" }]
    }));
  };

  const updateColor = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.map((c, i) => i === idx ? { ...c, [field]: value } : c)
    }));
  };

  const removeColor = (idx) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== idx)
    }));
  };

  const toggleLogo = (logoId) => {
    setFormData(prev => ({
      ...prev,
      logo_ids: prev.logo_ids.includes(logoId)
        ? prev.logo_ids.filter(id => id !== logoId)
        : [...prev.logo_ids, logoId]
    }));
  };

  if (loading) {
    return (
      <div className="p-8 space-y-4">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="brand-kits-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Brand Kits</h1>
          <p className="text-muted-foreground mt-1">
            Firmenfarben, Logos und Einstellungen als Paket speichern — sofort im Wizard nutzbar.
          </p>
        </div>
        <Button onClick={() => openEditor()} data-testid="create-brand-kit-btn">
          <Plus className="h-4 w-4 mr-2" />
          Neues Brand Kit
        </Button>
      </div>

      <Separator />

      {/* Kit List */}
      {kits.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Noch keine Brand Kits</h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              Erstelle dein erstes Brand Kit mit Firmenfarben, Logos und Voreinstellungen.
              Dein Team kann es direkt im Meta Ads Wizard verwenden.
            </p>
            <Button onClick={() => openEditor()} data-testid="create-first-brand-kit">
              <Plus className="h-4 w-4 mr-2" />
              Erstes Brand Kit erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kits.map(kit => (
            <Card
              key={kit.id}
              className="group hover:border-primary/50 transition-all cursor-pointer"
              onClick={() => openEditor(kit)}
              data-testid={`brand-kit-card-${kit.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Package className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{kit.name}</CardTitle>
                      {kit.owner_name && (
                        <CardDescription className="text-xs">von {kit.owner_name}</CardDescription>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      className="p-1.5 rounded-md hover:bg-destructive/10"
                      onClick={(e) => { e.stopPropagation(); handleDelete(kit.id); }}
                      data-testid={`delete-kit-${kit.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Color preview */}
                {kit.colors?.length > 0 && (
                  <div className="flex gap-1">
                    {kit.colors.slice(0, 8).map((c, i) => (
                      <div
                        key={i}
                        className="h-6 w-6 rounded-full border border-border"
                        style={{ backgroundColor: c.hex }}
                        title={`${c.name}: ${c.hex}`}
                      />
                    ))}
                    {kit.colors.length > 8 && (
                      <span className="text-xs text-muted-foreground self-center ml-1">+{kit.colors.length - 8}</span>
                    )}
                  </div>
                )}

                {/* Logo preview */}
                {kit.logos?.length > 0 && (
                  <div className="flex gap-2">
                    {kit.logos.slice(0, 3).map(logo => (
                      <div key={logo.id} className="h-8 w-8 rounded bg-muted flex items-center justify-center overflow-hidden">
                        {logo.image_data ? (
                          <img src={`data:image/png;base64,${logo.image_data}`} alt={logo.name} className="h-full w-full object-contain" />
                        ) : (
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Tags */}
                <div className="flex flex-wrap gap-1">
                  {kit.default_cta && kit.default_cta !== "Kein CTA" && (
                    <Badge variant="secondary" className="text-xs">{kit.default_cta}</Badge>
                  )}
                  {kit.tone && <Badge variant="outline" className="text-xs">{kit.tone}</Badge>}
                  {kit.image_preferences && (
                    <Badge variant="outline" className="text-xs">Bildwünsche</Badge>
                  )}
                </div>

                <div className="flex items-center text-xs text-muted-foreground pt-1">
                  <Pencil className="h-3 w-3 mr-1" />
                  Klicke zum Bearbeiten
                  <ChevronRight className="h-3 w-3 ml-auto" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingKit ? "Brand Kit bearbeiten" : "Neues Brand Kit"}</DialogTitle>
            <DialogDescription>
              Farben, Logos und Voreinstellungen für dein Team konfigurieren.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-2">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="kit-name">Name *</Label>
                <Input
                  id="kit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. RAN Gruppe, Musterkunde AG"
                  data-testid="brand-kit-name-input"
                />
              </div>

              <Separator />

              {/* Colors */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Firmenfarben</Label>
                  <Button size="sm" variant="outline" onClick={addColor} data-testid="add-brand-color">
                    <Plus className="h-3 w-3 mr-1" /> Farbe
                  </Button>
                </div>
                {formData.colors.length === 0 && (
                  <p className="text-sm text-muted-foreground">Noch keine Farben hinzugefügt.</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {formData.colors.map((color, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg">
                      <div className="relative">
                        <input
                          type="color"
                          value={color.hex}
                          onChange={(e) => updateColor(idx, "hex", e.target.value)}
                          className="absolute inset-0 w-10 h-10 opacity-0 cursor-pointer"
                          data-testid={`brand-color-picker-${idx}`}
                        />
                        <div
                          className="h-10 w-10 rounded-md border cursor-pointer"
                          style={{ backgroundColor: color.hex }}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Input
                          value={color.name}
                          onChange={(e) => updateColor(idx, "name", e.target.value)}
                          placeholder="Farbname"
                          className="h-7 text-sm"
                        />
                        <p className="text-xs font-mono text-muted-foreground">{color.hex}</p>
                      </div>
                      <button onClick={() => removeColor(idx)} className="p-1 hover:text-destructive">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Logos */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Logos</Label>
                {logos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Keine Logos in der Bibliothek.{" "}
                    <button onClick={() => navigate("/logos")} className="text-primary underline">Logo hochladen</button>
                  </p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {logos.map(logo => (
                      <div
                        key={logo.id}
                        className={`relative p-2 border rounded-lg cursor-pointer transition-all hover:border-primary ${
                          formData.logo_ids.includes(logo.id) ? "border-primary bg-primary/5 ring-1 ring-primary" : ""
                        }`}
                        onClick={() => toggleLogo(logo.id)}
                        data-testid={`brand-logo-${logo.id}`}
                      >
                        {formData.logo_ids.includes(logo.id) && (
                          <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                        <div className="aspect-square flex items-center justify-center">
                          {logo.image_data ? (
                            <img src={`data:image/png;base64,${logo.image_data}`} alt={logo.name} className="max-h-full max-w-full object-contain" />
                          ) : (
                            <Building2 className="h-8 w-8 text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-xs text-center truncate mt-1">{logo.name}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Logo Position */}
                <div className="space-y-2">
                  <Label>Logo-Position</Label>
                  <Select
                    value={formData.logo_position}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, logo_position: v }))}
                  >
                    <SelectTrigger data-testid="brand-logo-position">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOGO_POSITIONS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* CTA */}
              <div className="space-y-2">
                <Label>Standard-CTA</Label>
                <Select
                  value={formData.default_cta}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, default_cta: v }))}
                >
                  <SelectTrigger data-testid="brand-default-cta">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CTA_OPTIONS.map(cta => (
                      <SelectItem key={cta} value={cta}>{cta}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tone */}
              <div className="space-y-2">
                <Label>Tonalität / Stimmung</Label>
                <Input
                  value={formData.tone}
                  onChange={(e) => setFormData(prev => ({ ...prev, tone: e.target.value }))}
                  placeholder="z.B. professionell und modern, warm und einladend"
                  data-testid="brand-tone-input"
                />
              </div>

              {/* Image Preferences */}
              <div className="space-y-2">
                <Label>Bildwünsche</Label>
                <Textarea
                  value={formData.image_preferences}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_preferences: e.target.value }))}
                  placeholder="z.B. Junge Personen (25-35), lässiger Business-Look, diverse Gruppe, modernes Büro"
                  className="min-h-[60px]"
                  data-testid="brand-image-preferences"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notizen (intern)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Interne Hinweise für dein Team..."
                  className="min-h-[40px]"
                  data-testid="brand-notes-input"
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-brand-kit-btn">
              {saving ? "Speichere..." : editingKit ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
